import { type Component, type JSX } from 'solid-js';
import { CalendarIcon, ColumnsIcon, DeepDivesIcon, HeatmapIcon, MarketsIcon, NotesIcon, PortfoliosIcon, ReplayIcon, RoutineIcon, ScreenerIcon, UserIcon } from '~ui/icons';
import { PAGE_IDS, type PageId } from '~/stores/nav-store';
import AccountsPage from './accounts';
import ExposurePage from './exposure';
import LoansPage from './loans';
import PaymentsPage from './payments';
import ExplorePage from './explore';
import FundPage from './fund';
import ComparePage from './compare';
import HistoryPage from './history';
import FuturePage from './future';
import PlanPage from './plan';
import SandboxPage from './sandbox';
import ProfilePage from './profile';

export interface Page {
    id: PageId;
    label: string;
    // A thunk rather than an element, so each nav row builds its own icon
    // instead of every row sharing one node and only the last one rendering it.
    icon: () => JSX.Element;
    component: Component;
}

// `satisfies Record<PageId, …>` is what keeps this honest: adding an id to
// PAGE_IDS without a page here fails to compile rather than rendering a blank
// pane at runtime.
const DEFINITIONS = {
    accounts: { label: 'Accounts', icon: () => <PortfoliosIcon />, component: AccountsPage },
    exposure: { label: 'Exposure', icon: () => <HeatmapIcon />, component: ExposurePage },
    loans: { label: 'Loans', icon: () => <MarketsIcon />, component: LoansPage },
    payments: { label: 'Payments', icon: () => <CalendarIcon />, component: PaymentsPage },
    explore: { label: 'Explore', icon: () => <ScreenerIcon />, component: ExplorePage },
    // One fund, whole screen. Explore is the list and this is the page; they
    // share a selection, so switching tabs keeps whichever fund you were on.
    fund: { label: 'Fund', icon: () => <DeepDivesIcon />, component: FundPage },
    compare: { label: 'Compare', icon: () => <ColumnsIcon />, component: ComparePage },
    history: { label: 'History', icon: () => <ReplayIcon />, component: HistoryPage },
    // History's rewind glyph turned around: the two pages are the same
    // arithmetic pointed in opposite directions, and the icons should say so.
    future: { label: 'Future', icon: () => <span class='flex rotate-180'><ReplayIcon /></span>, component: FuturePage },
    plan: { label: 'Plan', icon: () => <NotesIcon />, component: PlanPage },
    // Not a plan with a different name: nothing here is stored, and it is built
    // by typing amounts rather than by recording trades.
    sandbox: { label: 'Sandbox', icon: () => <RoutineIcon />, component: SandboxPage },
    // Facts about the holder, not preferences — province and marginal rate
    // change every after-tax figure the app produces.
    profile: { label: 'Profile', icon: () => <UserIcon />, component: ProfilePage },
} satisfies Record<PageId, Omit<Page, 'id'>>;

// Order comes from PAGE_IDS so the nav and the id list cannot disagree.
export const PAGES: Page[] = PAGE_IDS.map((id) => ({ id, ...DEFINITIONS[id] }));

export const pageById = (id: PageId): Page => PAGES.find((p) => p.id === id)!;
